import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { usePreviousValue } from "../../hooks/usePreviousValue";
import { useSelector } from "react-redux";
import { getIsUserConfigLoaded } from "../../redux/SingleDataset/selectors";
import { useSearchParams } from "react-router-dom";
import { SEED_PARAMETER } from "../../helpers/constants/miscellaneous";

export type RightPanelMode = "summary" | "labels" | "";

type IDataPageRightPanelProvider = {
  mode: RightPanelMode;
  setMode: (m: RightPanelMode) => void;
  showPreviousMode: (options?: { fallback: RightPanelMode }) => void;
  closePanel: () => void;
};

const DataPageRightPanelContext = createContext<IDataPageRightPanelProvider>(
  {} as IDataPageRightPanelProvider,
);

export const useDataPageRightPanelContext = () =>
  useContext(DataPageRightPanelContext);

export const DataPageRightPanelProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const [searchParams] = useSearchParams();
  const isUserConfigLoaded = useSelector(getIsUserConfigLoaded);
  const [mode, setMode] = useState<RightPanelMode>("");
  useEffect(() => {
    if (!mode) {
      if (searchParams.has(SEED_PARAMETER)) {
        setMode("labels");
      } else if (isUserConfigLoaded) {
        setMode("summary");
      }
    }
  }, [isUserConfigLoaded, mode, searchParams]);
  const previousMode = usePreviousValue<RightPanelMode>(mode, []);
  const showPreviousMode = useCallback(
    (options?: { fallback: RightPanelMode }) => {
      setMode(previousMode || options?.fallback || "");
    },
    [previousMode],
  );
  const closePanel = useCallback(() => {
    setMode("");
  }, []);
  return (
    <DataPageRightPanelContext.Provider
      value={{ mode, setMode, showPreviousMode, closePanel }}
    >
      {children}
    </DataPageRightPanelContext.Provider>
  );
};
